import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";

import LoadingIndicator from "../../components/LoadingIndicator";

import {
  useQueryGetBrokerCommsAudit,
  useQueryGetBrokerTemplates,
} from "../../data/services/brokerCommsService";
import { useQueryGetCarrierList } from "../../data/services/carrierService";

const OccurredAtCell = (props) => {
  const raw = props.dataItem.occurredAt;
  if (!raw) return <td>-</td>;
  try {
    return <td>{new Date(raw).toLocaleString("en-US")}</td>;
  } catch {
    return <td>{raw}</td>;
  }
};

const BrokerCommsAuditGrid = () => {
  const [waveIdInput, setWaveIdInput] = React.useState("");
  const [carrierIdInput, setCarrierIdInput] = React.useState("");

  const [appliedWaveId, setAppliedWaveId] = React.useState(null);
  const [appliedCarrierId, setAppliedCarrierId] = React.useState(null);

  const [dataState, setDataState] = React.useState({ take: 10, skip: 0 });
  const [gridData, setGridData] = React.useState([]);
  const [masterData, setMasterData] = React.useState([]);

  const { isLoading, data: auditData, isError, error } =
    useQueryGetBrokerCommsAudit(appliedWaveId, appliedCarrierId);

  // Broker + Client templates so we can resolve names regardless of audience.
  const { data: brokerTemplatesData } = useQueryGetBrokerTemplates("Broker", null);
  const { data: clientTemplatesData } = useQueryGetBrokerTemplates("Client", null);
  const { data: carrierListData } = useQueryGetCarrierList();

  const templateMap = React.useMemo(() => {
    const map = {};
    [brokerTemplatesData?.data, clientTemplatesData?.data].forEach((list) => {
      (list || []).forEach((t) => {
        map[t.brokerTemplateId] = t;
      });
    });
    return map;
  }, [brokerTemplatesData, clientTemplatesData]);

  const carrierNameMap = React.useMemo(() => {
    const map = {};
    (carrierListData?.data || []).forEach((c) => {
      map[c.carrierId] = c.carrierName;
    });
    return map;
  }, [carrierListData]);

  React.useEffect(() => {
    if (!isLoading && auditData?.data) {
      const rows = auditData.data;
      setMasterData(rows);
      setGridData(process(rows.slice(0), dataState));
    }
  }, [isLoading, auditData]);

  React.useEffect(() => {
    if (masterData.length > 0) {
      setGridData(process(masterData.slice(0), dataState));
    }
  }, [dataState, masterData]);

  const dataStateChange = (event) => {
    setGridData(process(masterData.slice(0), event.dataState));
    setDataState(event.dataState);
  };

  const handleApply = () => {
    const waveId = waveIdInput.trim() !== "" ? Number(waveIdInput) : null;
    const carrierId =
      carrierIdInput.trim() !== "" ? Number(carrierIdInput) : null;
    setAppliedWaveId(waveId);
    setAppliedCarrierId(carrierId);
    setDataState({ take: 10, skip: 0 });
  };

  return (
    <div style={{ padding: "16px 24px" }}>
      <h2 style={{ marginTop: 0, marginBottom: 16 }}>Broker Comms Audit</h2>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "flex-end",
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
            Wave ID
          </label>
          <Input
            type="number"
            value={waveIdInput}
            onChange={(e) => setWaveIdInput(e.target.value)}
            style={{ width: 120 }}
            placeholder="e.g. 42"
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
            Carrier ID
          </label>
          <Input
            type="number"
            value={carrierIdInput}
            onChange={(e) => setCarrierIdInput(e.target.value)}
            style={{ width: 120 }}
            placeholder="e.g. 7"
          />
        </div>
        <Button themeColor="primary" onClick={handleApply}>
          Apply
        </Button>
      </div>

      {isError && (
        <div style={{ color: "#c4230a", marginBottom: 12 }}>
          {error?.message || "Failed to load audit records."}
        </div>
      )}

      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <Grid
          sortable={true}
          pageable={true}
          scrollable="none"
          data={gridData}
          {...dataState}
          onDataStateChange={dataStateChange}
        >
          <Column
            field="notificationWaveCarrierId"
            title="Wave Carrier ID"
            width={140}
          />
          <Column field="actor" title="Actor" />
          <Column field="action" title="Action" />
          <Column
            title="Carrier"
            width={150}
            cell={(props) => {
              const id = props.dataItem.carrierId;
              const name = carrierNameMap[id];
              return <td>{name ? `${name} (${id})` : id ?? "-"}</td>;
            }}
          />
          <Column
            title="Template"
            width={220}
            cell={(props) => {
              // Audit rows carry `templateId`; the template map is keyed by the
              // template list's `brokerTemplateId`, so resolve against templateId.
              const id = props.dataItem.templateId;
              const t = templateMap[id];
              if (!t) return <td>{id ?? "-"}</td>;
              return (
                <td>
                  {t.name}
                  <div style={{ fontSize: 11, color: "#666" }}>
                    v{props.dataItem.templateVersion ?? t.version} &middot; {t.audience}
                    {t.carrierId ? ` · Carrier ${carrierNameMap[t.carrierId] ?? t.carrierId}` : ""}
                  </div>
                </td>
              );
            }}
          />
          <Column
            field="recipientCount"
            title="Recipient Count"
            width={140}
          />
          <Column
            title="Occurred At"
            cell={OccurredAtCell}
            width={180}
          />
          <Column field="notes" title="Notes" />
        </Grid>
      )}
    </div>
  );
};

export default BrokerCommsAuditGrid;
